import { Component, OnInit, OnDestroy, inject, ViewEncapsulation, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent } from '@components/confirmation-modal/confirmation-modal.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map, filter, take, startWith, shareReplay } from 'rxjs/operators';
import { Organization } from '@models/organization.model';
import { UserService } from '@services/user.service';
import { OrganizationDetailsService } from '@services/organization-details.service';
import { UserAdminActions } from '@store/actions/user-admin/user-admin.actions';
import {
  selectOrgs,
  selectUserAdminFilteredOrgs
} from '@store/selectors/user-admin/user-admin.selector';
import { selectActiveUser } from '@store/selectors/user/user.selectors';
import { ROLE_GROUPS } from '../../constants/roles';
import { selectOrganizationLegacyId } from '@store/selectors/add-organization/add-organization.selectors';
import { MatTooltip } from '@angular/material/tooltip';
import { UserActions } from '@store/actions/user/user.actions';

@Component({
  selector: 'app-org-switcher',
  standalone: true,
  imports: [
    CommonModule,
    MatSelectModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    MatTooltip
  ],
  templateUrl: './org-switcher.component.html',
  styleUrls: ['./org-switcher.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class OrgSwitcherComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly userService = inject(UserService);
  private readonly orgDetailsService = inject(OrganizationDetailsService);
  private readonly actions$ = inject(Actions);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  activeUser$ = this.store.select(selectActiveUser);
  availableOrgs$ = this.store.select(selectUserAdminFilteredOrgs);
  organizationLegacyName$ = this.store.select(selectOrganizationLegacyId);

  orgSearchControl = new FormControl('');
  showDisabledControl = new FormControl(false);
  filteredOrgs$: Observable<Organization[]> = new Observable();

  isDropdownOpen = false;

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  isSuperUser$ = this.userService.hasRequiredRoles(ROLE_GROUPS.SUPER_USER);

  userAssignedOrgs$ = combineLatest([
    this.activeUser$,
    this.availableOrgs$,
    this.isSuperUser$
  ]).pipe(
    map(([activeUser, allOrgs, isSuperUser]) => {
      if (!allOrgs) {
        return [];
      }

      if (isSuperUser) {
        return allOrgs;
      }

      if (!activeUser?.loggedInUser?.organizationIds) {
        return [];
      }

      const userOrgs = allOrgs.filter(org =>
        activeUser.loggedInUser?.organizationIds.includes(Number(org.id))
      );

      return userOrgs.filter(org => org.statusID === 1000);
    })
  );

  canSwitchOrg$ = combineLatest([
    this.activeUser$,
    this.userAssignedOrgs$,
    this.userService.hasRequiredRoles(ROLE_GROUPS.ADMIN_ROLES),
    this.isSuperUser$
  ]).pipe(
    map(([activeUser, assignedOrgs, isAdmin, isSuperUser]) => {
      if (isSuperUser && assignedOrgs.length > 1) {
        return true;
      }
      return isAdmin && assignedOrgs.length > 1;
    })
  );

  currentOrgId$ = this.activeUser$.pipe(
    map(activeUser => activeUser?.orgId)
  );

  currentOrgName$ = combineLatest([
    this.userAssignedOrgs$,
    this.currentOrgId$
  ]).pipe(
    map(([orgs, currentOrgId]) => {
      if (!orgs || !currentOrgId) return '';
      const currentOrg = orgs.find(org => org.id == currentOrgId.toString());
      return currentOrg?.name || '';
    })
  );

  isLoading = false;
  private subs: Subscription[] = [];

  ngOnInit() {
    const orgLoadSub = combineLatest([
      this.activeUser$.pipe(filter(activeUser => !!activeUser?.orgId)),
      this.store.select(selectOrgs),
    ]).pipe(take(1)).subscribe(([, orgs]) => {
      if (!orgs || orgs.length === 0) {
        this.store.dispatch(UserAdminActions.pageLoaded());
      }
    });
    this.subs.push(orgLoadSub);

    this.filteredOrgs$ = combineLatest([
      this.userAssignedOrgs$,
      this.orgSearchControl.valueChanges.pipe(startWith(this.orgSearchControl.value)),
      this.showDisabledControl.valueChanges.pipe(startWith(this.showDisabledControl.value)),
      this.isSuperUser$
    ]).pipe(
      map(([orgs, searchTerm, showDisabled, isSuperUser]) => {
        if (!orgs) return [];

        let filteredOrgs = orgs;

        if (isSuperUser && !showDisabled) {
          filteredOrgs = filteredOrgs.filter(org => org.statusID !== 1001);
        }

        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          filteredOrgs = filteredOrgs.filter(org =>
            org.name.toLowerCase().includes(searchLower) ||
            org.id.toString().includes(searchTerm) ||
            org.legacyOrganizationID?.toLowerCase().toString().includes(searchLower)
          );
        }

        return filteredOrgs;
      }),
      shareReplay(1)
    );

    document.addEventListener('click', this.handleClickOutside.bind(this));
  }

  toggleDropdown() {
    if (!this.isLoading) {
      this.isDropdownOpen = !this.isDropdownOpen;

      if (this.isDropdownOpen) {
        setTimeout(() => {
          this.positionDropdown();
          this.searchInput?.nativeElement?.focus();
        }, 0);
      } else {
        this.orgSearchControl.setValue('');
      }
    }
  }

  private positionDropdown() {
    const trigger = document.querySelector('.custom-select-trigger') as HTMLElement;
    const panel = document.querySelector('.custom-dropdown-panel') as HTMLElement;

    if (!trigger || !panel) return;

    const triggerRect = trigger.getBoundingClientRect();
    const panelWidth = 600;
    const viewportWidth = window.innerWidth;

    panel.style.top = `${triggerRect.bottom + 8}px`;

    if (triggerRect.left + panelWidth > viewportWidth) {
      panel.style.right = `50px`;
      panel.style.left = 'auto';
    } else {
      panel.style.left = `${triggerRect.left}px`;
      panel.style.right = 'auto';
    }
  }

  closeDropdown() {
    this.isDropdownOpen = false;
    this.orgSearchControl.setValue('');
  }

  private handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const dropdownPanel = document.querySelector('.custom-dropdown-panel');
    const trigger = document.querySelector('.custom-select-trigger');

    if (this.isDropdownOpen &&
        dropdownPanel &&
        trigger &&
        !dropdownPanel.contains(target) &&
        !trigger.contains(target)) {
      this.closeDropdown();
    }
  }

  onOrgSelect(org: Organization) {
    if (!org || !org.id) return;

    const sub = combineLatest([
      this.isSuperUser$,
      this.userAssignedOrgs$,
      this.activeUser$.pipe(filter(activeUser => !!activeUser?.loggedInUser)),
    ]).pipe(take(1)).subscribe(([isSuperUser, allOrgs, activeUser]) => {
      if (org.statusID === 1001 && !isSuperUser) {
        this.snackBar.open(
          'Cannot switch to a disabled organization. Please contact your administrator.',
          'Close',
          { duration: 3000 }
        );
        return;
      }

      if (org.statusID === 1001 && isSuperUser) {
        const enabledOrgs = allOrgs.filter(o => o.statusID === 1000);

        if (enabledOrgs.length > 0) {
          this.snackBar.open(
            `"${org.name}" is disabled. Please enable it first using the toggle switch, or select an enabled organization.`,
            'Close',
            { duration: 4000 }
          );
          return;
        } else {
          this.snackBar.open(
            `Warning: All organizations are disabled. You can still access "${org.name}" to manage settings.`,
            'Close',
            { duration: 4000 }
          );
        }
      }

      const newOrgIdNumber = Number(org.id);

      if (activeUser && activeUser.orgId !== newOrgIdNumber) {
        localStorage.setItem('selectedOrgId', org.id);

        this.store.dispatch(
          UserActions.setUserOrganizationId({
            id: newOrgIdNumber
          })
        );

        this.closeDropdown();

        this.snackBar.open(
          `Switching to organization: ${org.name}`,
          'Close',
          { duration: 2000 }
        );

        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        this.closeDropdown();
      }

      this.isLoading = false;
    });

    this.subs.push(sub);
  }

  toggleOrgStatus(org: Organization, event: Event) {
    event.stopPropagation();

    const isCurrentlyDisabled = org.statusID === 1001;

    if (isCurrentlyDisabled) {
      this.applyOrgStatusChange(org, isCurrentlyDisabled);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: 'Disable Organization',
        content: `Disabling "${org.name}" will also deactivate any users whose only remaining active organization is this one. Continue?`,
        confirmText: 'Disable',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.applyOrgStatusChange(org, isCurrentlyDisabled);
      }
    });
  }

  private applyOrgStatusChange(org: Organization, isCurrentlyDisabled: boolean) {
    const action = isCurrentlyDisabled ? 'enabled' : 'disabled';

    const apiCall = isCurrentlyDisabled
      ? this.orgDetailsService.enableOrganization(org.id)
      : this.orgDetailsService.disableOrganization(org.id);

    apiCall.subscribe({
      next: () => {
        this.snackBar.open(
          `Organization "${org.name}" has been ${action}`,
          'Close',
          { duration: 3000 }
        );

        this.store.dispatch(UserAdminActions.pageLoaded());
      },
      error: (error: any) => {
        this.snackBar.open(
          `Failed to ${action === 'enabled' ? 'enable' : 'disable'} organization: ${error.message}`,
          'Close',
          { duration: 5000 }
        );
      }
    });
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub?.unsubscribe());
    document.removeEventListener('click', this.handleClickOutside.bind(this));
  }
}